import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SideNavigation, MobileNavigation, Header, Footer, DarkBackground } from './';
import SetupChecklistWidget from './SetupChecklistWidget';
import BackToClaudeWidget from './BackToClaudeWidget';
import { useUser, useUI } from '../contexts';
import { isBlockedUserId } from '../utils/userFlags';
import '../styles/Layout.css';

function Layout() {
    const { loggedIn, user } = useUser();
    const { showDarkBackground, sidebarCollapsed } = useUI();
    const location = useLocation();
    const navigate = useNavigate();
    const [innerWidth, setInnerWidth] = useState(window.innerWidth);
    
    useEffect(() => {
        const handleWindowResize = () => {
            setInnerWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    }, []);

    // Redirect any blocked user back to the root path, regardless of the page
    useEffect(() => {
        if (!loggedIn || !user || !user.userId) return;
        if (isBlockedUserId(user.userId) && location.pathname !== '/') {
            navigate('/', { replace: true });
        }
    }, [loggedIn, user, location.pathname, navigate]);
    
    // Determine if timeline should be shown (for agentic pages)
    const showTimeline = location.pathname.includes('/run-comps') || 
                        location.pathname.includes('/demos') ||
                        location.pathname.includes('/sample-report');
    
    // Hide footer on RunComps pages
    const hideFooter = location.pathname.includes('/run-comps') || 
                       location.pathname.includes('/demos') ||
                       location.pathname.includes('/sample-report') ||
                       location.pathname.includes('/run-comps-test');
    
    // Show side navigation on settings, profile, home, reports, automation, god-mode, and leads pages
    const showSideNavigation = location.pathname === '/settings' ||
                               location.pathname === '/profile' ||
                               location.pathname === '/' ||
                               location.pathname === '/reports' ||
                               location.pathname === '/automation' ||
                               location.pathname === '/god-mode' ||
                               location.pathname === '/integrations' ||
                               location.pathname === '/buyers' ||
                               location.pathname === '/deals' ||
                               location.pathname.startsWith('/deals/');
    
    // Show desktop side navigation only on screens >= 999px
    const showDesktopNav = showSideNavigation && innerWidth >= 999;
    
    const isAuthPage = location.pathname === '/sign-up' ||
                       location.pathname === '/login' ||
                       location.pathname === '/company-setup' ||
                       location.pathname === '/connect-ai' ||
                       location.pathname.startsWith('/onboarding');

    // Hide footer if either mobile or desktop navigation is present
    const shouldShowFooter = !hideFooter && !showSideNavigation && !isAuthPage;

    return (
        <div className={`layout-container ${showDarkBackground ? 'relative-container ' : ''}${!isAuthPage ? 'has-app-header' : ''}`}>
            {showDarkBackground && <DarkBackground />}
            {!isAuthPage && (
                <Header
                    loggedIn={loggedIn}
                    showTimeline={showTimeline}
                />
            )}
            <div className="layout-content-container">
                {showDesktopNav && <SideNavigation />}
                <div className={showDesktopNav ? (sidebarCollapsed ? 'layout-outlet-with-collapsed-nav' : 'layout-outlet-with-nav') : 'layout-outlet'}>
                    <Outlet />
                </div>
                {/* Sibling of the outlet (not inside it) so its position:fixed
                    isn't clipped by the outlet's overflow-x:clip. Renders only on
                    main app pages for incomplete accounts — gated internally. */}
                <SetupChecklistWidget />
                {/* Bottom-left counterpart: nudges Claude-origin accounts back
                    into Claude. Self-gated (signupSource === 'claude'). */}
                <BackToClaudeWidget />
            </div>
            {shouldShowFooter && <Footer />}
            {showSideNavigation && <MobileNavigation />}
        </div>
    );
}

export default Layout;
